import { z } from 'zod';

// Helper for optional, empty, or valid date strings
const optionalDateSchema = z.union([
  z.string().length(0),
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  z.null(),
  z.undefined(),
]);

// Helper for numeric strings with '$' and commas
const numericStringSchema = z.union([
  z.string().refine((val) => {
    if (val === '' || val === '$' || val === null || val === undefined) {
      return true;
    }
    const cleanValue = String(val).replace(/[$,]/g, '');
    return !Number.isNaN(parseFloat(cleanValue)) && Number.isFinite(Number(cleanValue));
  }, 'Must be a valid number'),
  z.number(),
  z.null(),
  z.undefined(),
]);

export const companyInfoSchema = z.object({
  clientName: z
    .string()
    .min(1, 'Company Name is required')
    .max(100, 'Company Name must be 100 characters or less')
    .nullable()
    .optional(),
  company_legal_name: z
    .string()
    .max(100, 'Company Legal Name must be 100 characters or less')
    .nullable()
    .optional(),
  company_registration: z.string().max(50, 'Must be 50 characters or less').nullable().optional(),
  secondary_company_registration: z
    .string()
    .max(50, 'Must be 50 characters or less')
    .nullable()
    .optional(),
  company_address: z.string().max(500, 'Must be 500 characters or less').nullable().optional(),
  company_website: z
    .string()
    .max(255, 'Must be 255 characters or less')
    .regex(/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/, 'Invalid website format')
    .or(z.literal(''))
    .nullable()
    .optional(),
  company_phone: z
    .string()
    .max(50, 'Must be 50 characters or less')
    .regex(/^[\d\s+()-]{8,}$/, 'Invalid phone number format')
    .or(z.literal(''))
    .nullable()
    .optional(),
  timezone: z.string().max(100, 'Must be 100 characters or less').nullable().optional(),
  company_contact_name: z.string().max(50, 'Must be 50 characters or less').nullable().optional(),
  company_contact_email: z
    .string()
    .max(255, 'Must be 255 characters or less')
    .email('Invalid email format')
    .or(z.literal(''))
    .nullable()
    .optional(),
  company_contact_role: z.string().max(50, 'Must be 50 characters or less').nullable().optional(),
  contact_facsimile: z
    .string()
    .max(50, 'Must be 50 characters or less')
    .regex(/^[\d\s+()-]{8,}$/, 'Invalid facsimile number format')
    .or(z.literal(''))
    .nullable()
    .optional(),
  liability_insurer: z.string().max(100, 'Must be 100 characters or less').nullable().optional(),
  liability_insurance_expiry: optionalDateSchema,
  liability_insurance_per_claim: numericStringSchema,
  liability_insurance_aggregate: numericStringSchema,
  indemnity_insurer: z.string().max(100, 'Must be 100 characters or less').nullable().optional(),
  indemnity_insurer_expiry: optionalDateSchema,
  indemnity_insurer_per_claim: numericStringSchema,
  indemnity_insurer_aggregate: numericStringSchema,
  workers_compensation_insurer_1: z
    .string()
    .max(100, 'Must be 100 characters or less')
    .nullable()
    .optional(),
  workers_compensation_expiry_1: optionalDateSchema,
  workers_compensation_insurer_2: z
    .string()
    .max(100, 'Must be 100 characters or less')
    .nullable()
    .optional(),
  workers_compensation_expiry_2: optionalDateSchema,
  workers_compensation_insurer_3: z
    .string()
    .max(100, 'Must be 100 characters or less')
    .nullable()
    .optional(),
  workers_compensation_expiry_3: optionalDateSchema,
  workers_compensation_insurer_4: z
    .string()
    .max(100, 'Must be 100 characters or less')
    .nullable()
    .optional(),
  workers_compensation_expiry_4: optionalDateSchema,
});

export type CompanyFormData = z.infer<typeof companyInfoSchema>;

export const initialCompanyFormData: CompanyFormData = {
  clientName: '',
  company_legal_name: '',
  company_registration: '',
  secondary_company_registration: '',
  company_address: '',
  company_website: '',
  company_phone: '',
  timezone: 'UTC',
  company_contact_name: '',
  company_contact_email: '',
  company_contact_role: '',
  contact_facsimile: '',
  liability_insurer: '',
  liability_insurance_expiry: '',
  liability_insurance_per_claim: '',
  liability_insurance_aggregate: '',
  indemnity_insurer: '',
  indemnity_insurer_expiry: '',
  indemnity_insurer_per_claim: '',
  indemnity_insurer_aggregate: '',
  workers_compensation_insurer_1: '',
  workers_compensation_expiry_1: '',
  workers_compensation_insurer_2: '',
  workers_compensation_expiry_2: '',
  workers_compensation_insurer_3: '',
  workers_compensation_expiry_3: '',
  workers_compensation_insurer_4: '',
  workers_compensation_expiry_4: '',
};
